import { useMemo } from 'react';

import type { AxiosInstance } from 'axios';

import { TrackableItem, TrackableList } from 'types/trackables';
import { CursorPaginated, CursorPaginationParams } from 'types/pagination';

import { useAPI } from 'hooks/api/useAPI';
import { useAuthenticatedQuery } from 'hooks/api/useAuthenticatedQuery';

type TrackableMetadataResponse = {
  items: Record<string, TrackableItem>;
  lists: Record<string, TrackableList>;
};

async function fetchTrackableMetadata(
  applicationId: string,
  trackableIds: string[],
  client: AxiosInstance
): Promise<TrackableMetadataResponse> {
  if (!trackableIds.length) {
    return { items: {}, lists: {} };
  }

  // Fetch all items using cursor pagination (server limit is 150 per page)
  const itemMap = new Map<string, TrackableItem>();
  let nextCursor: string | null = null;
  const ITEMS_PER_PAGE = 150;

  do {
    const params: CursorPaginationParams & { trackable_item_ids: string[] } = {
      trackable_item_ids: trackableIds,
      pagination: 'cursor',
      limit: ITEMS_PER_PAGE,
    };

    if (nextCursor) {
      params.next_cursor = nextCursor;
    }

    const { data: itemsResponse } = await client.get<CursorPaginated<TrackableItem>>(
      `/apps/${applicationId}/trackable_items`,
      { params }
    );

    for (const item of itemsResponse.data) {
      itemMap.set(item.id, item);
    }

    nextCursor = itemsResponse.next_cursor;
  } while (nextCursor !== null);

  // Extract unique list IDs from all fetched items
  const listIds = Array.from(
    new Set(Array.from(itemMap.values()).map((item) => item.trackable_list_id))
  );

  if (listIds.length === 0) {
    return {
      items: Object.fromEntries(itemMap),
      lists: {},
    };
  }

  // Fetch all lists using cursor pagination
  const listMap = new Map<string, TrackableList>();
  nextCursor = null;

  do {
    const params: CursorPaginationParams & { trackable_list_ids: string[] } = {
      trackable_list_ids: listIds,
      pagination: 'cursor',
      limit: ITEMS_PER_PAGE,
    };

    if (nextCursor) {
      params.next_cursor = nextCursor;
    }

    const { data: listsResponse } = await client.get<CursorPaginated<TrackableList>>(
      `/apps/${applicationId}/trackables`,
      { params }
    );

    for (const list of listsResponse.data) {
      listMap.set(list.id, list);
    }

    nextCursor = listsResponse.next_cursor;
  } while (nextCursor !== null);

  return {
    items: Object.fromEntries(itemMap),
    lists: Object.fromEntries(listMap),
  };
}

export function useTrackableNames(trackableIds: string[]) {
  const ONE_MINUTE_IN_MS = 60 * 1000;
  const { applicationID, client } = useAPI();

  const ids = useMemo(() => {
    const filtered = trackableIds.filter((id) => Boolean(id));
    if (!filtered.length) {
      return [];
    }
    const unique = Array.from(new Set(filtered));
    unique.sort();
    return unique;
  }, [trackableIds]);

  const query = useAuthenticatedQuery<TrackableMetadataResponse>(
    ['trackable-names', applicationID, ids],
    async () => {
      if (!applicationID || ids.length === 0) {
        return { items: {}, lists: {} };
      }

      return fetchTrackableMetadata(applicationID, ids, client);
    },
    {
      enabled: Boolean(applicationID) && ids.length > 0,
      staleTime: ONE_MINUTE_IN_MS,
    }
  );

  return {
    ...query,
    ids,
  };
}
